// Contenido de la landing pública (sebbia.cl). Todo el texto vive acá para que la página se pueda
// revisar —y corregir— sin leer JSX.
//
// ⚠️ `requiereLanzamiento: true` marca lo que la página PROMETE y el producto todavía NO hace
// (se implementa antes del lanzamiento oficial). `scripts/verificar_lanzamiento.py` lista esas
// marcas y falla mientras queden: corre en el gate de producción, así la landing no sale a vender
// una función que no existe. Al terminar una integración, se borra la marca de su ítem.
//
// `proximamente: true` es la otra salida honesta: la función se MUESTRA, rotulada "Próximamente".
// No bloquea el deploy porque no promete que ya exista. Al lanzarla se borra el flag (y, si aplica,
// se pone `disponible: true` en `PORTALES_PUBLICACION`).

export const WHATSAPP_NUMERO = "56959752446";
export const WHATSAPP_MENSAJE = "Hola, vengo de sebbia.cl y quiero agendar una demo de SEBBIA.";
export const whatsappUrl = (mensaje = WHATSAPP_MENSAJE) =>
  `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(mensaje)}`;

export const NAV_LINKS = [
  { href: "#como-funciona", label: "Cómo funciona" },
  { href: "#producto", label: "Producto" },
  { href: "#planes", label: "Planes" },
  { href: "#preguntas", label: "Preguntas" },
];

// ── Mockups: datos de ejemplo ────────────────────────────────────────────────────────────────────
// Cifras coherentes entre sí (el neto del voucher sale de sus propias líneas). La UF es la del
// parámetro de la propuesta comercial; los mockups la rotulan como ejemplo.
export const EJEMPLO = {
  periodo: "Septiembre 2026",
  ufLabel: "UF 9 Sep 2026",
  uf: 40901.94,
  arriendoUf: 18.5,
  honorariosPct: 8,
  mantencion: 45000,
};
EJEMPLO.arriendo = Math.round(EJEMPLO.arriendoUf * EJEMPLO.uf);
EJEMPLO.honorarios = Math.round((EJEMPLO.arriendo * EJEMPLO.honorariosPct) / 100);
EJEMPLO.neto = EJEMPLO.arriendo - EJEMPLO.honorarios - EJEMPLO.mantencion;

// ── "Tu mes, resuelto" ───────────────────────────────────────────────────────────────────────────
// El mes es la columna de la página: cada paso cuenta el dolor y cómo lo resuelve SEBBIA, y los
// detalles chilenos van en el paso donde se usan, no en una lista aparte.
export const PASOS_MES = [
  {
    key: "cobrar",
    dia: 1,
    titulo: "Cobrar",
    corto: "Cobrar",
    antes: "Mandas decenas de mensajes recordando el arriendo, uno por uno.",
    ahora:
      "Programas los avisos de cobro una vez: en cada fecha SEBBIA los envía por correo, o te los deja listos para aprobar.",
    chips: [{ label: "Envíos programados" }, { label: "Aprobación antes de enviar" }],
  },
  {
    key: "registrar",
    dia: 5,
    titulo: "Registrar pagos",
    corto: "Registrar",
    antes: "Cruzas la cartola del banco con una planilla, fila por fila.",
    ahora:
      "Registras cada pago con su fecha real. La multa por atraso se calcula sola y el voucher para el arrendatario queda listo.",
    chips: [{ label: "Multa por fecha real de pago" }, { label: "Voucher al arrendatario" }],
  },
  {
    key: "liquidar",
    dia: 9,
    titulo: "Liquidar y cerrar",
    corto: "Liquidar",
    antes: "Buscas la UF, recalculas honorarios y cruzas los dedos para que nadie toque la planilla.",
    ahora:
      "Cada contrato pacta su UF —día 9 o cierre de mes— y sus honorarios. Al cerrar, el mes queda listo para consultarlo cuando quieras.",
    chips: [
      { label: "UF día 9 o de cierre" },
      { label: "Honorarios con 4 bases de cálculo" },
      { label: "Reajuste IPC" },
      { label: "Historial por período" },
    ],
  },
  {
    key: "rendir",
    dia: 10,
    titulo: "Rendir al propietario",
    corto: "Rendir",
    antes: "Armas cada liquidación en PDF y la adjuntas a mano, correo por correo.",
    ahora:
      "Envías todas las liquidaciones en un clic, cada una con su PDF, y cada propietario ve su estado de cuenta en su portal.",
    chips: [{ label: "Envío masivo con PDF" }, { label: "Portal del propietario" }, { label: "Nómina de pagos" }],
  },
];

// ── Módulos (bento) ──────────────────────────────────────────────────────────────────────────────
export const MODULOS = [
  {
    key: "propiedades",
    titulo: "Propiedades y contratos",
    texto: "Ficha completa de cada propiedad: contrato, propietario, arrendatario, vigencia y el historial de cada mes.",
  },
  {
    key: "portales",
    titulo: "Portal del propietario y del arrendatario",
    texto: "Cada uno entra con su cuenta y ve su estado de cuenta, sus liquidaciones y sus comprobantes.",
  },
  {
    key: "reportes",
    titulo: "Reportes e indicadores",
    texto: "Ocupación, recaudación e ingresos de la agencia, exportables a Excel y PDF.",
  },
  {
    key: "condominios",
    titulo: "Condominios",
    texto: "Agrupa unidades por edificio o condominio y comparte sus datos entre todas.",
  },
  {
    key: "publicacion",
    titulo: "Publica en Portal Inmobiliario desde SEBBIA",
    texto: "Tus avisos salen de tu cartera, sin reescribirlos: publica, actualiza o pausa desde SEBBIA. Usa los cupos de tu plan del portal.",
    proximamente: true,
  },
  {
    key: "ventas",
    titulo: "Ventas",
    texto: "Comisión de la agencia y neto al propietario de cada venta, en el mismo lugar que tus arriendos.",
  },
];

// Publicar en Portal Inmobiliario usa el paquete que la corredora contrata en el portal (sin cupo, la
// API de Mercado Libre rechaza el aviso): SEBBIA publica, no regala la publicación. TocToc e iCasas se
// muestran como "próximamente" hasta tener su integración (no se nombran otros portales sin acuerdo).
export const PORTALES_PUBLICACION = [
  { nombre: "Portal Inmobiliario", disponible: false },
  { nombre: "TocToc", disponible: false },
  { nombre: "iCasas", disponible: false },
];

// ── Cómo empiezas ────────────────────────────────────────────────────────────────────────────────
export const COMO_EMPIEZAS = [
  {
    titulo: "Agenda una demo",
    texto: "Una videollamada en la que te mostramos SEBBIA con casos como los de tu cartera.",
  },
  {
    titulo: "Cargamos tu cartera",
    texto: "Te ayudamos a pasar propiedades, propietarios y arrendatarios desde tus planillas.",
  },
  {
    titulo: "Cierras tu primer mes",
    texto: "Te acompañamos en tu primer cierre y en el primer envío de liquidaciones.",
  },
];

// ── Planes ───────────────────────────────────────────────────────────────────────────────────────
// Fuente: "Propuesta Com. Sebbia.xlsx" (2026-09-28) con los ajustes aprobados: usuario extra 0,5 UF
// parejo, packs de 10 propiedades a 0,25 UF y precio fundador. Precios en UF + IVA mensual.
export const PLAN_BASE_INCLUYE = [
  { label: "Propiedades, contratos y personas" },
  { label: "Resumen de período y liquidaciones" },
  { label: "Nómina de pagos a propietarios" },
  { label: "Portal del propietario y del arrendatario" },
  { label: "Envío masivo y programado de correos" },
  { label: "Condominios" },
  { label: "Publicación en Portal Inmobiliario, con tu plan del portal", proximamente: true },
];

export const PLANES = [
  {
    key: "starter",
    nombre: "Starter",
    para: "Para corredores independientes que parten.",
    uf: "0,7",
    propiedades: "Hasta 20 propiedades",
    usuarios: "1 usuario",
    extras: ["Reportes completos"],
  },
  {
    key: "basico",
    nombre: "Básico",
    para: "Para corredoras con una cartera en crecimiento.",
    uf: "2",
    propiedades: "Hasta 70 propiedades",
    usuarios: "2 usuarios",
    extras: ["Reportes completos", "Ventas: comisión y neto al propietario"],
    destacado: true,
  },
  {
    key: "pro",
    nombre: "Pro",
    para: "Para equipos que administran carteras grandes.",
    uf: "4",
    propiedades: "Hasta 180 propiedades",
    usuarios: "3 usuarios",
    extras: ["Todo lo de Básico", "Equipo: roles y permisos por usuario"],
  },
  {
    key: "enterprise",
    nombre: "Enterprise",
    para: "Para operaciones con varias agencias.",
    uf: "5",
    propiedades: "Hasta 300 propiedades",
    usuarios: "Usuarios a medida",
    extras: ["Todo lo de Pro", "Subagencias o sucursales (próximamente)", "Sobre 300 propiedades, precio por propiedad"],
  },
];

export const PLAN_ADICIONALES = [
  { label: "Usuario adicional", precio: "0,5 UF", nota: "En Starter, máximo 1." },
  { label: "Pack de 10 propiedades", precio: "0,25 UF", nota: "Máximo 2 en Starter y 4 en Básico." },
];

export const FUNDADORES = {
  titulo: "Programa Agencias Fundadoras",
  texto: "Las primeras 10 agencias que se sumen a SEBBIA tienen 25% de descuento durante 12 meses.",
};

// ── Confianza ────────────────────────────────────────────────────────────────────────────────────
// Sólo hechos verificables del producto: nada de "seguridad bancaria" ni cifras de clientes.
export const CONFIANZA = [
  { key: "aislamiento", titulo: "Tu cartera, sólo para tu equipo", texto: "Tus propiedades, clientes y cifras los ve tu corredora y nadie más." },
  { key: "roles", titulo: "Cada uno ve lo suyo", texto: "Cada persona de tu equipo entra con su cuenta y ve lo que le corresponde." },
  { key: "cierre", titulo: "Historial de cada mes, a mano", texto: "¿Un propietario pregunta por marzo? Abres su liquidación tal como se la enviaste, en un clic." },
  { key: "marca", titulo: "Tu sello en cada documento", texto: "Tu logo y tus colores en la app, las liquidaciones y los vouchers; tu nombre en cada correo." },
];

// ── Preguntas frecuentes ─────────────────────────────────────────────────────────────────────────
export const PREGUNTAS = [
  {
    q: "¿Necesito instalar algo?",
    a: "No. SEBBIA funciona en el navegador, en el computador o en el celular.",
  },
  {
    q: "¿Puedo traer mi cartera desde Excel?",
    a: "Sí. Te ayudamos a cargar propiedades, propietarios y arrendatarios desde tus planillas antes de tu primer cierre.",
  },
  {
    q: "¿Cómo se calcula la UF de cada contrato?",
    a: "Cada contrato define si usa la UF del día 9 o la del cierre de mes. SEBBIA toma ese valor para el período y lo convierte a pesos en cobros, pagos y liquidaciones.",
  },
  {
    q: "¿Cómo funciona el reajuste IPC?",
    a: "Cada contrato en pesos define su periodicidad: trimestral, semestral o anual. En cada aniversario SEBBIA calcula el reajuste con el IPC oficial y te lo propone; lo confirmas en un clic y el arrendatario recibe el aviso, con copia al propietario.",
  },
  {
    q: "¿Qué ven mis propietarios y arrendatarios?",
    a: "Cada uno entra a su portal con su propia cuenta: el propietario ve sus liquidaciones y su estado de cuenta; el arrendatario, sus cobros y comprobantes.",
  },
  {
    q: "¿Qué pasa si supero las propiedades de mi plan?",
    a: "Puedes sumar packs de 10 propiedades o pasar al plan siguiente cuando te convenga.",
  },
  {
    q: "¿Qué significa ser Agencia Fundadora?",
    a: "Las primeras 10 agencias que se suman a SEBBIA tienen 25% de descuento sobre su plan durante sus primeros 12 meses.",
  },
  {
    q: "¿Los precios incluyen IVA?",
    a: "No. Los precios están en UF más IVA, por mes.",
  },
  {
    q: "¿Puedo usar la marca de mi corredora?",
    a: "Sí. Tu logo y tus colores aparecen en la app y en las liquidaciones, y el nombre de tu agencia en los correos que reciben tus clientes.",
  },
];

export const RANGOS_PROPIEDADES = ["Hasta 20", "21 a 70", "71 a 180", "181 a 300", "Más de 300"];

// ── Promesa: "Tu cartera crece. Tu tiempo se mantiene." ─────────────────────────────────────────
export const PROMESA = {
  eyebrow: "Nuestro compromiso",
  titulo: "Tu cartera crece.",
  tituloTenue: "Tu tiempo se mantiene.",
  bajada: "Nuestro compromiso es hacer crecer tu cartera, no tus horas de trabajo.",
  texto:
    "SEBBIA automatiza y centraliza tu operación inmobiliaria para que administres más propiedades sin que tu equipo tenga que dedicarles proporcionalmente más horas.",
  puntos: [
    "Los cálculos del mes se hacen solos: UF, multas, honorarios y netos.",
    "Los envíos se programan una vez y se repiten cada mes.",
    "Cada propietario y arrendatario se atiende desde su portal.",
    "Los reajustes IPC se calculan solos en cada aniversario del contrato, con la periodicidad que elijas: trimestral, semestral o anual.",
    { texto: "Publicas en Portal Inmobiliario desde tu cartera en SEBBIA, sin reescribir cada aviso.", proximamente: true },
  ],
};

export const LEMA = "Tu gestión inmobiliaria más feliz :)";

// ── Marca propia ────────────────────────────────────────────────────────────────────────────────
export const MARCA_PROPIA = {
  eyebrow: "Marca propia",
  titulo: "Tu marca, tus colores.",
  tituloTenue: "Un software que se siente tuyo desde el primer día.",
  texto:
    "Elige el color de tu corredora y súmale tu logo. Tu equipo trabaja en un sistema con tu identidad, y tus propietarios reciben liquidaciones con tu marca.",
};

// ── Franja de cifras (al comienzo de "Tu mes, resuelto") ─────────────────────────────────────────
// Sólo cifras que el PRODUCTO sostiene: nada de métricas de clientes (todavía no hay) ni tiempos
// no medidos. "De días a 1 clic" y no "de 3 días": el 3 no lo midió nadie; cuando las Agencias
// Fundadoras den el dato real, se reemplaza por el número medido.
// La precisión se afirma SOBRE LOS DATOS CARGADOS: el cálculo es exacto, y un dato mal tipeado es
// del corredor. Así lo dicen también los términos de uso (cláusula 6).
export const CIFRAS = {
  titulo: "De días de planillas a 1 clic.",
  items: [
    { cifra: "1", unidad: "clic", titulo: "Para enviar todas tus liquidaciones", texto: "Cada propietario recibe la suya, con su PDF." },
    { cifra: "0", unidad: "planillas", titulo: "Para cerrar tu mes", texto: "Cobros, pagos y liquidaciones en un solo lugar." },
    { cifra: "100", unidad: "%", titulo: "Precisión en cada cálculo", texto: "Cifras exactas sobre los datos de tus contratos." },
  ],
};
